import { IDR, magnifyValue } from '@diamond/shared/decimal-util-web';
import { CartTotalBody, CartTotalResponse } from '@diamond/shared/types';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { dinero, toDecimal } from 'dinero.js';
import { useMemo } from 'react';
import { useDebounce } from 'usehooks-ts';

import { postCartTotal } from '../queries';
import {
  useAuthStore,
  useCartStore,
  useShipToStore,
  useVoucherStore,
} from '../stores';

export const CART_TOTAL_QUERY_KEY = 'CartTotal';
export const CART_TOTAL_PRODUCT_DETAIL_QUERY_KEY = 'CartTotalProductDetail';

function createPriceMagnifier(amount: number) {
  const price = Number(
    toDecimal(
      dinero({
        amount: amount,
        currency: IDR,
      })
    )
  );
  const priceMath = Math.ceil(price);
  const priceMagnify = magnifyValue(priceMath, 'idr');

  return priceMagnify;
}

export type CartTotalSinglePromo = CartTotalResponse['promos'][number];

function groupPromoByDivision(promos: CartTotalResponse['promos']) {
  return promos.reduce((acc, curr) => {
    acc[curr.division] = curr;
    return acc;
  }, {} as Record<CartTotalSinglePromo['division'], CartTotalSinglePromo>);
}

function selectDataWithPriceMagnifier(data: CartTotalResponse) {
  return {
    ...data,
    originalPriceMagnify: createPriceMagnifier(data.original_total),
    finalPriceMagnify: createPriceMagnifier(data.final_total),
    savedPriceMagnify: createPriceMagnifier(data.saved_total),
    groupedPromo: groupPromoByDivision(data.promos),
  };
}

export function normalizeCartTotalPayload(
  payload: CartTotalBody
): CartTotalBody {
  const products = payload.products
    ? [...payload.products]
        .filter((product) => {
          const quantity = product.quantity;
          return (
            typeof quantity === 'number' && !isNaN(quantity) && quantity > 0
          );
        })
        .map((product) => ({
          product_id: product.product_id,
          quantity: product.quantity,
        }))
    : [];

  const voucher_codes = payload.voucher_codes
    ? [...payload.voucher_codes].sort((a, b) => a.localeCompare(b))
    : undefined;

  const sortedProducts = products.sort((a, b) => {
    if (a.product_id === b.product_id) {
      return a.quantity - b.quantity;
    }

    return a.product_id.localeCompare(b.product_id);
  });

  return {
    ...payload,
    products: sortedProducts,
    voucher_codes,
  };
}

type CartStoreState = ReturnType<typeof useCartStore.getState>;

export const selectCartProductsForTotals = (
  state: CartStoreState
): CartTotalBody['products'] =>
  state.cartItems
    .map((cartId) => {
      const entry = state.cartRecord[cartId];
      if (!entry) {
        return null;
      }

      const quantity =
        entry.quantity ?? entry.product.minimum_order_quantity ?? 1000;

      if (typeof quantity !== 'number' || quantity <= 0) {
        return null;
      }

      return {
        product_id: entry.product.id,
        quantity,
      };
    })
    .filter(
      (entry): entry is CartTotalBody['products'][number] => entry !== null
    );

export function areCartProductsEqual(
  cartProducts1: CartTotalBody['products'],
  cartProducts2: CartTotalBody['products']
) {
  if (cartProducts1.length !== cartProducts2.length) {
    return false;
  }

  const sorted1 = [...cartProducts1].sort((a, b) =>
    a.product_id.localeCompare(b.product_id)
  );
  const sorted2 = [...cartProducts2].sort((a, b) =>
    a.product_id.localeCompare(b.product_id)
  );

  for (let i = 0; i < sorted1.length; i++) {
    const first = sorted1[i];
    const second = sorted2[i];
    if (
      first.product_id !== second.product_id ||
      first.quantity !== second.quantity
    ) {
      return false;
    }
  }

  return true;
}

export function cartTotalQueryOptions(
  access_token: string,
  payload: CartTotalBody,
  enabled?: boolean,
  queryKeyPrefix: string = CART_TOTAL_QUERY_KEY
) {
  const normalizedPayload = normalizeCartTotalPayload(payload);

  // Don't create query options if there are no products
  if (!normalizedPayload.products || normalizedPayload.products.length === 0) {
    return {
      queryKey: [queryKeyPrefix, normalizedPayload],
      queryFn: () =>
        Promise.resolve({
          original_total: 0,
          final_total: 0,
          saved_total: 0,
          promos: [],
        }),
      retry: false,
      enabled: false,
      initialData: {
        original_total: 0,
        final_total: 0,
        saved_total: 0,
        promos: [],
      },
      select: selectDataWithPriceMagnifier,
    };
  }

  return queryOptions({
    queryKey: [queryKeyPrefix, normalizedPayload],
    queryFn: async () => {
      return postCartTotal(access_token, normalizedPayload);
    },
    retry: false,
    enabled: enabled !== false && normalizedPayload.products.length > 0,
    initialData: {
      original_total: 0,
      final_total: 0,
      saved_total: 0,
      promos: [],
    },
    placeholderData: (previousData) => previousData,
    select: selectDataWithPriceMagnifier,
  });
}

/**
 * Custom equality function for voucher codes
 * Prevents unnecessary re-renders when voucher array reference changes but content is same
 */
export function areVoucherCodesEqual(
  vouchers1: { id: string; code: string }[],
  vouchers2: { id: string; code: string }[]
): boolean {
  if (vouchers1.length !== vouchers2.length) return false;
  return vouchers1.every((v, i) => v.code === vouchers2[i]?.code);
}

export function useCartTotal() {
  const accessToken = useAuthStore((state) => state.access_token);
  const warehouse = useShipToStore((state) => state.selectedShipToId);

  // Use custom equality to prevent re-renders when voucher content hasn't changed
  const appliedVouchers = useVoucherStore(
    (state) => state.appliedVouchers,
    areVoucherCodesEqual
  );

  // Memoize voucher codes extraction to maintain stable reference
  const voucherCodes = useMemo(
    () => appliedVouchers.map((v) => v.code),
    [appliedVouchers]
  );

  const products = useCartStore(
    selectCartProductsForTotals,
    areCartProductsEqual
  );

  const payload: CartTotalBody = useMemo(
    () => ({
      warehouse_id: warehouse,
      is_return_promos: true,
      products,
      voucher_codes: voucherCodes.length > 0 ? voucherCodes : undefined,
    }),
    [products, warehouse, voucherCodes]
  );

  const debouncedPayload = useDebounce(payload, 500);
  const isEnabled = useMemo(
    () =>
      Boolean(
        accessToken &&
          warehouse &&
          debouncedPayload.products &&
          debouncedPayload.products.length > 0
      ),
    [accessToken, warehouse, debouncedPayload.products]
  );
  return useQuery(
    cartTotalQueryOptions(accessToken, debouncedPayload, isEnabled)
  );
}

// Should be only used in Product Detail
export function useCartTotalProductDetail(
  productId: string | undefined,
  quantity: number | undefined,
  enabled = true
) {
  const accessToken = useAuthStore((state) => state.access_token);
  const warehouse = useShipToStore((state) => state.selectedShipToId);

  // Memoize the payload to prevent unnecessary re-creation
  const payload: CartTotalBody = useMemo(
    () => ({
      warehouse_id: warehouse,
      is_single_promo_only: false,
      is_return_promos: true,
      products:
        productId && quantity ? [{ product_id: productId, quantity }] : [],
    }),
    [productId, quantity, warehouse]
  );

  const debouncedPayload = useDebounce(payload, 500);

  // Enhanced enabled condition to prevent hook execution when not needed
  const isEnabled = useMemo(
    () =>
      enabled &&
      !!accessToken &&
      !!productId &&
      !!quantity &&
      quantity > 0 &&
      !!warehouse &&
      payload.products.length > 0,
    [
      enabled,
      accessToken,
      productId,
      quantity,
      warehouse,
      payload.products.length,
    ]
  );

  // Use the query options with enhanced enabled condition
  return useQuery({
    ...cartTotalQueryOptions(
      accessToken,
      debouncedPayload,
      isEnabled,
      CART_TOTAL_PRODUCT_DETAIL_QUERY_KEY
    ),
    enabled: isEnabled,
  });
}
